package one.healthium.base.utils

import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.daysUntil
import kotlinx.datetime.format
import kotlinx.datetime.format.DayOfWeekNames
import kotlinx.datetime.format.MonthNames
import kotlinx.datetime.isoDayNumber
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import loco.GENERAL_RELATIVE_DATE_DAYS_AGO_FORMAT
import loco.GENERAL_RELATIVE_DATE_TODAY
import loco.GENERAL_RELATIVE_DATE_YESTERDAY
import one.healthium.loco.LString
import one.healthium.loco.asLString
import kotlin.math.abs
import kotlin.time.Clock
import kotlin.time.Instant

fun Instant.relativeDate(): LString {
    return when (val daysDiff = daysBetween(Clock.System.now())) {
        0 -> loco.Res.string.GENERAL_RELATIVE_DATE_TODAY.asLString()
        1 -> loco.Res.string.GENERAL_RELATIVE_DATE_YESTERDAY.asLString()
        else -> loco.Res.string.GENERAL_RELATIVE_DATE_DAYS_AGO_FORMAT.asLString(daysDiff)
    }
}

fun Instant.daysBetween(other: Instant): Int {
    return abs(this.toLocalDateTime(TimeZone.currentSystemDefault()).date.daysUntil(other.toLocalDateTime(TimeZone.currentSystemDefault()).date))
}

fun Instant.toLocalDateTime(): LocalDateTime {
    return toLocalDateTime(TimeZone.currentSystemDefault())
}

fun Instant.toLocalDate(): LocalDate {
    return toLocalDateTime(TimeZone.currentSystemDefault()).date
}

fun LocalDateTime.isSameDay(dateTime: LocalDate): Boolean {
    return this.date.daysUntil(dateTime) == 0
}

fun InstantToLocalDate(instant: Instant, timeZone: TimeZone = TimeZone.currentSystemDefault()): LocalDate {
    return instant.toLocalDateTime(timeZone).date
}

fun LocalDateToInstant(localDate: LocalDate, timeZone: TimeZone = TimeZone.currentSystemDefault()): Instant {
    return localDate.atStartOfDayIn(timeZone)
}

fun LocalDate.startOfWeek(): LocalDate {
    val dayOfWeek = this.dayOfWeek.isoDayNumber
    return this.minus(DatePeriod(days = dayOfWeek - 1))
}

fun LocalDate.endOfWeek(): LocalDate {
    val dayOfWeek = this.dayOfWeek.isoDayNumber
    return this.plus(DatePeriod(days = 7 - dayOfWeek))
}

fun LocalDate.isToday(): Boolean {
    val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    return today == this
}

fun Long.toInstant(): Instant {
    return Instant.fromEpochMilliseconds(this)
}

fun localizedMonthNames(abbreviated: Boolean = false): MonthNames =
    MonthNames(Month.entries.map { it.getLocalizedName(abbreviated) })

fun localizedDayOfWeekNames(abbreviated: Boolean = false): DayOfWeekNames =
    DayOfWeekNames(DayOfWeek.entries.map { it.getLocalizedName(abbreviated, narrow = false) })

fun Month.localizedName(abbreviated: Boolean = false): String =
    getLocalizedName(abbreviated)

fun DayOfWeek.localizedName(abbreviated: Boolean = false, uppercase: Boolean = false, narrow: Boolean = false): String {
    val name = getLocalizedName(abbreviated = abbreviated, narrow = narrow)
    return if (uppercase) name.uppercase() else name
}

private fun dayOfWeekFormat() = LocalDateTime.Format {
    dayOfWeek(localizedDayOfWeekNames())
}

fun Instant.dayOfWeek(): String {
    return this.toLocalDateTime(TimeZone.currentSystemDefault()).format(dayOfWeekFormat())
}